import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Card from "components/card";
import Button from "components/button";
import { getEvent, rsvpEvent, cancelRsvpEvent } from "services/events";
import { useAuthContext } from "hooks/AuthContext";
import { useToast } from "components/Toast";

const UserEventShow: React.FC = () => {
  const { id } = useParams();
  const { user } = useAuthContext();
  const toast = useToast();
  const [event, setEvent] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!id) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const res = await getEvent(id);
        const data = res.data?.data ?? res.data ?? null;
        if (mounted) setEvent(data);
      } catch (err) {
        console.error(err);
        if (mounted) setEvent(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [id]);

  const userRsvpStatus = () => {
    return (
      event?.user_rsvp?.status ||
      event?.userRsvp?.status ||
      (Array.isArray(event?.rsvps)
        ? event.rsvps.find((r: any) => r.user_id === user?.id)?.status
        : undefined)
    );
  };

  const handleRsvp = async (status: "going" | "maybe") => {
    if (!id) return;
    setSaving(true);
    const prev = event;
    try {
      setEvent((ev: any) => ({ ...ev, user_rsvp: { status } }));
      await rsvpEvent(id, status);
      // refresh event from server to get counts and rsvps
      try {
        const fresh = await getEvent(id);
        setEvent(fresh.data?.data ?? fresh.data ?? prev);
      } catch (e) {
        // ignore refresh error
      }
      toast.showSuccess?.("RSVP successful");
    } catch (err) {
      console.error(err);
      setEvent(prev);
      const e = err as any;
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to RSVP";
      toast.showError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    setSaving(true);
    const prev = event;
    try {
      setEvent((ev: any) => ({ ...ev, user_rsvp: undefined }));
      await cancelRsvpEvent(id);
      try {
        const fresh = await getEvent(id);
        setEvent(fresh.data?.data ?? fresh.data ?? prev);
      } catch (e) {}
      toast.showSuccess?.("RSVP cancelled");
    } catch (err) {
      console.error(err);
      setEvent(prev);
      const msg =
        (err as any)?.response?.data?.error ||
        (err as any)?.response?.data?.message ||
        (err as any)?.message ||
        "Failed to cancel RSVP";
      toast.showError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  const goingCount =
    event?.going_count ??
    (Array.isArray(event?.rsvps)
      ? event.rsvps.filter((r: any) => r.status === "going").length
      : 0);
  const maybeCount =
    event?.maybe_count ??
    (Array.isArray(event?.rsvps)
      ? event.rsvps.filter((r: any) => r.status === "maybe").length
      : 0);
  const available = event?.capacity
    ? Math.max(0, event.capacity - goingCount)
    : null;

  return (
    <Card extra="w-full p-4">
      {loading ? (
        <div>Loading...</div>
      ) : !event ? (
        <div className="text-gray-500">Event not found.</div>
      ) : (
        <div>
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                {event.name || event.title}
              </h1>
              <div className="text-sm text-gray-500">
                {event.event_date}{" "}
                {event.event_time ? `• ${event.event_time}` : ""}
                {event.location ? (
                  <span className="ml-2">• {event.location}</span>
                ) : null}
              </div>
              <div className="mt-1 text-xs text-gray-400">
                {goingCount} going{maybeCount ? ` • ${maybeCount} maybe` : ""}
                {event.capacity ? ` • capacity ${event.capacity}` : ""}
                {available !== null ? ` • ${available} spots left` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {userRsvpStatus() ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    {saving ? "Working..." : "Cancel RSVP"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleRsvp("going")}
                    disabled={saving}
                  >
                    {saving ? "Working..." : "Going"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRsvp("maybe")}
                    disabled={saving}
                  >
                    {saving ? "Working..." : "Maybe"}
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="prose dark:prose-invert max-w-none">
            <p>{event.description ?? event.body ?? event.content}</p>
          </div>

          {Array.isArray(event.rsvps) && event.rsvps.length > 0 ? (
            <div className="mt-6">
              <h3 className="text-lg font-semibold">Attendees</h3>
              <ul className="mt-2 space-y-2">
                {event.rsvps.map((r: any) => (
                  <li
                    key={r.id}
                    className="flex items-center justify-between rounded bg-white p-2 dark:bg-navy-700"
                  >
                    <div>
                      <div className="font-medium">
                        {r.user?.firstname || r.user?.name || r.user?.email}
                      </div>
                      <div className="text-xs text-gray-500">{r.status}</div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {r.created_at
                        ? new Date(r.created_at).toLocaleString()
                        : ""}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
};

export default UserEventShow;
